/**
 * Module-level tap flush mutex — prevents double-apply on tab switch / unmount.
 * Batches carry idempotency keys so keepalive / WebView kills cannot double-credit.
 */
import api from '../api/client';
import { parseMoney } from './clickerMerge';

const LOCAL_STORAGE_KEY_TAPS = 'sync_taps';
const LOCAL_STORAGE_KEY_ENERGY = 'sync_energy';
const LOCAL_STORAGE_KEY_INFLIGHT = 'sync_taps_inflight';
const LOCAL_STORAGE_KEY_BATCH = 'sync_taps_batch_id';

/** Prefer fewer, larger requests — server still rate-caps (~12 taps/s). */
const BATCH_DEFAULT = 80;
/** After waiting out the rate window, send another large chunk. */
const BATCH_AFTER_WAIT = 60;
/** Soft cap on loop iterations for a normal flush (hide/nav). */
const MAX_PARTIAL_RETRIES = 12;
/** Hard drain (spend / prestige) may take longer under the human rate cap. */
const MAX_DRAIN_RETRIES = 60;
const TAP_RATE_PER_SEC = 12;

let flushChain = Promise.resolve();
let flushing = false;
/** Taps claimed from LS but not yet settled by a confirmed server response. */
let inFlightTaps = 0;
let inFlightBatchId = null;
/** Listeners notified after each successfully settled tap batch. */
const tapBatchListeners = new Set();

/**
 * Subscribe to mid-flush tap responses so React balance stays in lockstep
 * with shrinking outstanding taps.
 * @param {(data: object) => void} fn
 * @returns {() => void}
 */
export function subscribeTapBatches(fn) {
  if (typeof fn !== 'function') return () => {};
  tapBatchListeners.add(fn);
  return () => tapBatchListeners.delete(fn);
}

function emitTapBatch(data) {
  if (!data || typeof data !== 'object') return;
  tapBatchListeners.forEach((fn) => {
    try {
      fn(data);
    } catch (_) {
      /* listener errors must not abort the flush chain */
    }
  });
}

function readInt(key) {
  return (
    parseInt(typeof localStorage !== 'undefined' ? localStorage.getItem(key) : '0', 10) || 0
  );
}

function makeBatchId() {
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 11)}`;
}

function persistInFlight(taps, batchId) {
  inFlightTaps = Math.max(0, taps);
  inFlightBatchId = inFlightTaps > 0 ? batchId || inFlightBatchId : null;
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY_INFLIGHT, String(inFlightTaps));
    if (inFlightBatchId) {
      localStorage.setItem(LOCAL_STORAGE_KEY_BATCH, inFlightBatchId);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEY_BATCH);
    }
  } catch (_) {
    /* ignore */
  }
}

function clearInFlight() {
  persistInFlight(0, null);
}

// Recover an interrupted in-flight batch. Keep taps + batch_id so a retry is
// idempotent on the server — do NOT fold into pending without the key.
try {
  const orphan = readInt(LOCAL_STORAGE_KEY_INFLIGHT);
  const batchId =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem(LOCAL_STORAGE_KEY_BATCH)
      : null;
  if (orphan > 0) {
    inFlightTaps = orphan;
    inFlightBatchId = batchId || makeBatchId();
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY_BATCH, inFlightBatchId);
    } catch (_) {
      /* ignore */
    }
  } else {
    inFlightTaps = 0;
    inFlightBatchId = null;
  }
} catch (_) {
  inFlightTaps = 0;
  inFlightBatchId = null;
}

export function getPendingTaps() {
  return readInt(LOCAL_STORAGE_KEY_TAPS);
}

/** Queued in LS + currently in-flight batch (UI floor while flushing). */
export function getOutstandingTaps() {
  return getPendingTaps() + inFlightTaps;
}

export function isTapFlushing() {
  return flushing;
}

/**
 * Claim up to `batch` pending taps. If an unresolved in-flight batch exists,
 * return that instead (idempotent retry) — never invent a second batch_id.
 */
function claimOrRetry(batch = BATCH_DEFAULT) {
  if (inFlightTaps > 0 && inFlightBatchId) {
    return { sent: inFlightTaps, batchId: inFlightBatchId, retried: true };
  }
  const total = getPendingTaps();
  if (total <= 0) return { sent: 0, batchId: null, retried: false };
  const sent = Math.min(total, batch);
  localStorage.setItem(LOCAL_STORAGE_KEY_TAPS, String(total - sent));
  const batchId = makeBatchId();
  persistInFlight(sent, batchId);
  return { sent, batchId, retried: false };
}

/** Server confirmed it did not apply these taps — safe to re-queue without batch_id. */
function abandonInFlightToPending() {
  if (inFlightTaps <= 0) return;
  const n = inFlightTaps;
  clearInFlight();
  localStorage.setItem(LOCAL_STORAGE_KEY_TAPS, String(getPendingTaps() + n));
}

function settleApplied(sent, applied) {
  const unapplied = Math.max(0, sent - applied);
  clearInFlight();
  if (unapplied > 0) {
    localStorage.setItem(LOCAL_STORAGE_KEY_TAPS, String(getPendingTaps() + unapplied));
  }
}

/** Drop queued taps that can never be credited (e.g. energy already empty). */
export function discardOutstandingTaps() {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY_TAPS, '0');
  } catch (_) {
    /* ignore */
  }
  clearInFlight();
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function rateWaitMs(applied, unapplied) {
  // Wait for a meaningful window so the next request can carry a large batch.
  if (applied <= 0) return 1100;
  const need = Math.min(Math.max(unapplied, BATCH_AFTER_WAIT), BATCH_DEFAULT);
  return Math.min(2500, Math.max(900, Math.ceil((need / TAP_RATE_PER_SEC) * 1000)));
}

/**
 * Flush pending taps. Concurrent callers share one chain.
 * @param {{
 *   keepalive?: boolean,
 *   onBatch?: (data: object) => void,
 *   drain?: boolean,
 * }} [opts]
 * @returns {Promise<object|null>}
 */
export function flushPendingTaps(opts = {}) {
  const keepalive = Boolean(opts.keepalive);
  const drain = Boolean(opts.drain);
  const onBatch = typeof opts.onBatch === 'function' ? opts.onBatch : null;
  const maxRetries = drain ? MAX_DRAIN_RETRIES : MAX_PARTIAL_RETRIES;

  const run = async () => {
    let last = null;
    let rateLimitTries = 0;
    let partialRetries = 0;
    let nextBatch = BATCH_DEFAULT;
    flushing = true;
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { sent, batchId } = claimOrRetry(nextBatch);
        if (sent <= 0) break;

        try {
          const payload = {
            taps: sent,
            batch_id: batchId,
            timestamp: Math.floor(Date.now() / 1000),
            interval_ms: 1800,
          };
          const responseData = await api.clickerTap(payload, { keepalive });
          last = responseData;

          if (responseData?.blocked) {
            // Antibot freeze — leave queue as-is for later; drop this batch id
            // so a future unfreeze does not replay a frozen credit.
            abandonInFlightToPending();
            return responseData;
          }

          if (responseData?.busy) {
            // Lock miss — server did not apply. Safe to retry with a new claim.
            abandonInFlightToPending();
            await sleep(400);
            continue;
          }

          const applied = Math.max(
            0,
            Math.min(sent, parseInt(responseData.applied_taps ?? sent, 10) || 0)
          );
          settleApplied(sent, applied);

          // Never write raw server energy while taps remain — that inflated LS
          // when Main unmounted mid-flush and remounted with a phantom pool.
          if (responseData.energy != null) {
            const enNow = parseMoney(responseData.energy, NaN);
            const pptNow = parseMoney(responseData.coin_per_click, 0.01);
            const left = getOutstandingTaps();
            let storeEn = enNow;
            if (Number.isFinite(enNow) && left > 0 && Number.isFinite(pptNow) && pptNow > 0) {
              storeEn = Math.max(0, enNow - left * pptNow);
            }
            if (Number.isFinite(storeEn)) {
              localStorage.setItem(LOCAL_STORAGE_KEY_ENERGY, storeEn.toFixed(2));
            }
          }

          emitTapBatch(responseData);
          if (onBatch) {
            try {
              onBatch(responseData);
            } catch (_) {
              /* ignore */
            }
          }

          const unapplied = sent - applied;
          if (applied === 0 || unapplied > 0) {
            // Empty energy: remaining optimistic taps will never apply, but they
            // keep subtracting from the energy bar forever (stuck at 0). Drop them.
            const enNow = parseMoney(responseData.energy, NaN);
            const pptNow = parseMoney(responseData.coin_per_click, 0.01);
            if (
              applied === 0 &&
              Number.isFinite(enNow) &&
              Number.isFinite(pptNow) &&
              pptNow > 0 &&
              enNow < pptNow
            ) {
              discardOutstandingTaps();
              break;
            }
            if (keepalive) break;
            if (getOutstandingTaps() <= 0) break;
            partialRetries += 1;
            if (partialRetries >= maxRetries) {
              // Give up for this session — don't leave a zombie queue pinning energy at 0
              if (
                Number.isFinite(enNow) &&
                Number.isFinite(pptNow) &&
                pptNow > 0 &&
                enNow < pptNow * 2
              ) {
                discardOutstandingTaps();
              }
              break;
            }
            await sleep(rateWaitMs(applied, unapplied));
            nextBatch = BATCH_AFTER_WAIT;
            continue;
          }

          nextBatch = BATCH_DEFAULT;
          partialRetries = 0;
          rateLimitTries = 0;
          if (getOutstandingTaps() <= 0) break;
          // Brief yield only — avoid hammering with empty gaps between full applies
          await sleep(80);
        } catch (err) {
          // Unknown whether the server applied — KEEP inflight + batch_id for
          // an idempotent retry. Never fold into pending without the key.
          if (err?.status === 429) {
            rateLimitTries += 1;
            if (keepalive || rateLimitTries >= 5) break;
            await sleep(1500);
            continue;
          }
          if (keepalive) break;
          throw err;
        }
      }
      return last;
    } finally {
      flushing = false;
      // Do NOT move unresolved inflight back into pending — that caused
      // double-credits when the server had already applied the batch.
    }
  };

  flushChain = flushChain.then(run, run);
  return flushChain;
}

/**
 * Block until the tap queue is empty (or timeout). Use before spends / prestige.
 * @returns {Promise<{ ok: boolean, last: object|null, remaining: number }>}
 */
export async function awaitDrainPendingTaps({ timeoutMs = 28000 } = {}) {
  const deadline = Date.now() + Math.max(1000, timeoutMs);
  let last = null;
  while (getOutstandingTaps() > 0 && Date.now() < deadline) {
    last = await flushPendingTaps({ drain: true });
    if (getOutstandingTaps() <= 0) break;
    await sleep(350);
  }
  return {
    ok: getOutstandingTaps() <= 0,
    last,
    remaining: getOutstandingTaps(),
  };
}

export { LOCAL_STORAGE_KEY_TAPS, LOCAL_STORAGE_KEY_ENERGY };
